import { useState, useEffect } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useGoogleLogin } from '@react-oauth/google';
import { Calendar as CalendarIcon, Plus, RefreshCw, X, MapPin, CheckCircle, Edit3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const localizer = momentLocalizer(moment);

export default function CalendarView() {
  const [events, setEvents] = useState([]);
  const [googleToken, setGoogleToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  
  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [visitDate, setVisitDate] = useState(moment().format('YYYY-MM-DD'));
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [purpose, setPurpose] = useState('');
  
  const [selectedEvent, setSelectedEvent] = useState(null);
  const navigate = useNavigate();

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;

  // Load internal visits
  useEffect(() => {
    fetchInternalEvents();
    
    // Fetch customers for the dropdown
    fetch('/api/customers')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setCustomers(data.data);
        }
      });
  }, []);

  const fetchInternalEvents = async () => {
    setIsLoading(true);
    try {
      const url = (user?.role === 'admin' || user?.role === 'super_admin') 
        ? '/api/visits'
        : `/api/visits?user_id=${user?.id}`;

      const [visitsRes, placesRes] = await Promise.all([
        fetch(url),
        fetch(`/api/google_places?email=${user?.google_email || ''}`)
      ]);
      
      let allEvents = [];

      try {
        const visitsData = await visitsRes.json();
        if (visitsData.status === 'success' && visitsData.data) {
          const formattedVisits = visitsData.data.map(item => ({
            id: `visit_${item.id}`,
            title: `[เข้าพบ] ${item.customer_name}`,
            start: new Date(item.start_time),
            end: new Date(item.end_time),
            purpose: item.purpose,
            address: item.address,
            status: item.status,
            isVisit: true
          }));
          allEvents = [...allEvents, ...formattedVisits];
        }
      } catch (err) {
        console.error("Error parsing visits data:", err);
      }

      try {
        const placesData = await placesRes.json();
        if (placesData.status === 'success' && placesData.data) {
          let plannedPlaces = placesData.data.filter(p => p.is_planned == 1 && p.planned_date);
          
          // Filter by team if not admin
          if (user?.role !== 'admin' && user?.role !== 'super_admin' && user?.team_name) {
            plannedPlaces = plannedPlaces.filter(p => p.saved_by_team === user.team_name);
          }

          const formattedPlaces = plannedPlaces.map(p => {
            // Default to all day event or starting at 9 AM
            const startDate = new Date(`${p.planned_date}T09:00:00`);
            const endDate = new Date(`${p.planned_date}T10:00:00`);
            
            return {
              id: `place_${p.id}`,
              db_id: p.id,
              place_id: p.place_id,
              lat: p.lat,
              lng: p.lng,
              title: `[Plan] ${p.name}`,
              start: startDate,
              end: endDate,
              purpose: 'Sales Lead / รายการโปรดที่วางแผนไว้',
              address: p.address,
              status: 'planned',
              isVisit: false,
              isPlan: true,
              planned_date: p.planned_date,
              raw_data: p
            };
          });
          
          allEvents = [...allEvents, ...formattedPlaces];
        }
      } catch (err) {
        console.error("Error parsing places data:", err);
      }

      setEvents(allEvents);
    } catch (error) {
      console.error("Error fetching internal events:", error);
    }
    setIsLoading(false);
  };

  const handleCreateVisit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) {
      alert("กรุณาเลือกลูกค้า");
      return;
    }

    const startDateTime = `${visitDate}T${startTime}:00`;
    const endDateTime = `${visitDate}T${endTime}:00`;

    setIsLoading(true);
    try {
      // Save directly to our database only
      const response = await fetch('/api/visits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          customer_id: selectedCustomer,
          start_time: startDateTime,
          end_time: endDateTime,
          purpose: purpose
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        alert("บันทึกนัดหมายสำเร็จ!");
        setShowModal(false);
        setPurpose('');
        fetchInternalEvents(); // Refresh calendar
      } else {
        alert("เกิดข้อผิดพลาดในการบันทึกนัดหมาย: " + data.message);
      }
    } catch (error) {
      console.error(error);
      alert("เกิดข้อผิดพลาดในการเชื่อมต่อระบบ");
    }
    setIsLoading(false);
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#3174ad'; // Default blue
    
    if (event.isVisit) {
      backgroundColor = '#eb0a1e'; // Toyota Red for our visits
    } else if (event.isPlan) {
      backgroundColor = '#3b82f6'; // Blue for planned google places
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block'
      }
    };
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleShowLocation = () => {
    if (selectedEvent.isPlan) {
      navigate('/', { 
        state: { 
          searchResult: { 
            type: 'saved_place', 
            data: selectedEvent.raw_data
          } 
        } 
      });
    }
  };

  const handleMarkVisited = async () => {
    if (!selectedEvent || !selectedEvent.isPlan) return;
    setIsLoading(true);
    try {
      // Unplan it or mark it visited. For now, we'll unplan it or change status if you have one.
      // Since we don't have is_visited column natively added yet, we will just unplan it so it's removed from Plan To Visit list
      const res = await fetch('/api/google_places/' + selectedEvent.db_id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedEvent.db_id,
          action: 'mark_visited'
        })
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert('อัปเดตสถานะสำเร็จ (ลบออกจากแผนที่ต้องไป)');
        setSelectedEvent(null);
        fetchInternalEvents();
      } else {
        alert('เกิดข้อผิดพลาด: ' + data.message);
      }
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการเชื่อมต่อ');
    }
    setIsLoading(false);
  };

  const handleUpdatePlannedDate = async (newDate) => {
    if (!selectedEvent || !selectedEvent.isPlan) return;
    setIsLoading(true);
    try {
      const res = await fetch('/api/google_places/' + selectedEvent.db_id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selectedEvent.db_id,
          action: 'plan_visit',
          planned_date: newDate
        })
      });
      const data = await res.json();
      if (data.status === 'success') {
        setSelectedEvent(prev => ({ ...prev, planned_date: newDate }));
        fetchInternalEvents();
      } else {
        alert('เกิดข้อผิดพลาด: ' + data.message);
      }
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการเชื่อมต่อ');
    }
    setIsLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--color-primary)' }}>ปฏิทินวางแผนเข้าพบลูกค้า</h2>
          <p style={{ margin: 0 }}>ดูนัดหมายและสร้างแผนการเข้าพบ (ข้อมูลภายในระบบเท่านั้น)</p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button className="btn btn-secondary" onClick={() => fetchInternalEvents()} disabled={isLoading}>
            <RefreshCw size={18} style={{ marginRight: '0.5rem' }} className={isLoading ? 'spin' : ''} />
            รีเฟรช
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} />
            เพิ่มนัดหมายเข้าพบ
          </button>
        </div>
      </div>

      <div className="card" style={{ flex: 1, padding: '1rem', minHeight: '600px' }}>
        <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            defaultView="month"
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleEventClick}
            views={['month', 'week', 'day', 'agenda']}
            messages={{
              next: "ถัดไป",
              previous: "ก่อนหน้า",
              today: "วันนี้",
              month: "เดือน",
              week: "สัปดาห์",
              day: "วัน",
              agenda: "กำหนดการ"
            }}
          />
      </div>

      {/* Modal for Event Details */}
      {selectedEvent && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="card" style={{ width: '400px', maxWidth: '90%', position: 'relative' }}>
            <button 
              onClick={() => setSelectedEvent(null)}
              style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={24} color="#666" />
            </button>
            
            <h3 style={{ marginBottom: '0.5rem', color: selectedEvent.isPlan ? '#3b82f6' : '#eb0a1e' }}>
              {selectedEvent.title}
            </h3>
            <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '1.5rem' }}>{selectedEvent.address}</p>

            {selectedEvent.isPlan && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: '0.2rem' }}>แก้ไขวันที่เข้าพบ</label>
                  <input 
                    type="date" 
                    value={selectedEvent.planned_date || ''}
                    onChange={(e) => handleUpdatePlannedDate(e.target.value)}
                    className="input-field"
                    style={{ padding: '0.5rem' }}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button 
                    onClick={handleShowLocation}
                    style={{ flex: 1, backgroundColor: '#f3f4f6', color: '#1f2937', border: '1px solid #d1d5db', padding: '0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontWeight: 600 }}
                  >
                    <MapPin size={16} /> ดูตำแหน่งบนแผนที่
                  </button>
                  
                  <button 
                    onClick={handleMarkVisited}
                    disabled={isLoading}
                    style={{ flex: 1, backgroundColor: '#10b981', color: 'white', border: 'none', padding: '0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontWeight: 600 }}
                  >
                    <CheckCircle size={16} /> เข้าพบแล้ว
                  </button>
                </div>
              </div>
            )}
            
            {!selectedEvent.isPlan && (
              <div style={{ marginBottom: '1rem' }}>
                <p><strong>วัตถุประสงค์:</strong> {selectedEvent.purpose}</p>
                <p><strong>เวลา:</strong> {moment(selectedEvent.start).format('HH:mm')} - {moment(selectedEvent.end).format('HH:mm')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal for Creating Visit */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="card" style={{ width: '500px', maxWidth: '90%', position: 'relative' }}>
            <button 
              onClick={() => setShowModal(false)}
              style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={24} color="#666" />
            </button>
            
            <h3 style={{ marginBottom: '1.5rem', color: 'var(--color-primary)' }}>สร้างแผนเข้าพบลูกค้า</h3>
            
            <form onSubmit={handleCreateVisit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>เลือกลูกค้า <span style={{color:'red'}}>*</span></label>
                <select 
                  className="input-field" 
                  value={selectedCustomer} 
                  onChange={e => setSelectedCustomer(e.target.value)}
                  required
                >
                  <option value="">-- เลือกลูกค้า --</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.update_sales_team || 'No Team'})</option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>วันที่ <span style={{color:'red'}}>*</span></label>
                  <input 
                    type="date" 
                    className="input-field" 
                    value={visitDate} 
                    onChange={e => setVisitDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>เวลาเริ่ม <span style={{color:'red'}}>*</span></label>
                  <input 
                    type="time" 
                    className="input-field" 
                    value={startTime} 
                    onChange={e => setStartTime(e.target.value)}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>เวลาเสร็จสิ้น <span style={{color:'red'}}>*</span></label>
                  <input 
                    type="time" 
                    className="input-field" 
                    value={endTime} 
                    onChange={e => setEndTime(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>รายละเอียด / จุดประสงค์</label>
                <textarea 
                  className="input-field" 
                  rows="3" 
                  value={purpose} 
                  onChange={e => setPurpose(e.target.value)}
                  placeholder="เช่น นำเสนอสินค้าใหม่, ติดตามงาน..."
                ></textarea>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  ยกเลิก
                </button>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? 'กำลังบันทึก...' : 'บันทึกนัดหมาย'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
